/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */
/* eslint-disable no-else-return */
import React, { Suspense, lazy, useContext } from 'react';
import PropTypes from 'prop-types';
import styles from 'gatsby-core-theme/src/components/molecules/module/module.module.scss';
import Content from 'gatsby-core-theme/src/components/molecules/content';
import Toplist from 'gatsby-core-theme/src/components/organisms/toplist';
import Anchor from 'gatsby-core-theme/src/components/organisms/anchor/template-one';
import { Context } from '~context/MainProvider.js';
import Cards from '../../../../components/organisms/cards';
import ModuleTitle from '../../atoms/module-title';
import AdminButton from '~atoms/admin/button';
import { anchorLink, removeSymbols } from '~helpers/strings';

const Modules = ({ module, page, index, pageContext, serverData, modulePosition }) => {
  const { admin } = useContext(Context) || {};

  const GetModuleComponent = (moduleItem) => {
    const { items, name } = moduleItem || {};

    switch (name) {
      case 'content':
        return Content;
      case 'top_list':
        return Toplist;
      case 'archive':
        if (!items || items.length === 0) return null;
        return lazy(() => import('~organisms/archive'));
      case 'counter':
        return lazy(() => import('~molecules/counter'));
      case 'cards':
      case 'cards_v2':
        return Cards;
      case 'contact_form':
        return lazy(() => import('~atoms/contact-form'));
      case 'newsletter_form':
      case 'newsletter':
        return lazy(() => import('gatsby-core-theme/src/components/molecules/newsletter'));
      case 'dynamic_newsletter':
        return lazy(() => import('../../../../components/organisms/dynamic-newsletter'));
      case 'pros_and_cons':
        return lazy(() => import('~molecules/pros-cons'));
      case 'faq':
        if (moduleItem?.display_front_end === '1') {
          return lazy(() => import('~atoms/faq'));
        }
        return null;
      case 'accordion':
        return lazy(() => import('~organisms/accordion'));
      case 'anchor':
        if (items && items?.length > 0) {
          return Anchor;
        }
        return null;
      case 'carousel':
        if (
          items?.length > 0 && // has items
          items?.filter((item) => item.image === null).length === 0 // not filled with null items
        ) {
          return lazy(() => import('~organisms/carousel'));
        }
        return null;
      case 'bonus':
        return lazy(() => import('~molecules/bonus/template-one'));
      case 'image':
        return lazy(() => import('~atoms/image'));
      case 'spotlights':
        if (module?.mode === 'image_text') {
          if (module?.style === 'template_two') {
            return lazy(() => import('~molecules/spotlights_v2/image-text/template-two'));
          } else if (module?.style === 'template_three') {
            return lazy(() => import('~molecules/spotlights_v2/image-text/template-three'));
          } else if (module?.style === 'template_four') {
            return lazy(() => import('~molecules/spotlights_v2/image-text/template-four'));
          }
          return lazy(() => import('~molecules/spotlights_v2/image-text/template-one'));
        } else if (module?.mode === 'icon') {
          return lazy(() => import('~molecules/spotlights_v2/icon/template-one'));
        } else if (module?.mode === 'image') {
          if (module?.style === 'template_two') {
            return lazy(() => import('~molecules/spotlights_v2/image/template-two'));
          }
          return lazy(() => import('~molecules/spotlights_v2/image/template-one'));
        }
        return null;
      case 'review_summary':
        return lazy(() => import('../../../../components/molecules/operator-summary'));
      default:
        return null;
    }
  };

  const disableModuleIntro = module.name === 'spotlights' && module?.style === 'template_four';

  const ModuleComponent = GetModuleComponent(module);

  // eslint-disable-next-line no-nested-ternary
  const anchorLabel = module.anchor_label
    ? removeSymbols(anchorLink(module.anchor_label))
    : module.name === 'archive'
    ? 'archiveLabel'
    : `module_${module.module_value_id}`;

  const extraProps = () => {
    switch (module?.name) {
      case 'bonus':
        return {
          operator: module?.value,
          logo: module?.value?.logo_url,
          hasLink: true,
        };
      case 'newsletter_form':
        return {
          anchorLabel,
          isSticky: false,
        };
      default:
        return {
          anchorLabel,
        };
    }
  };

  const moduleClassName = `${module.name}Module`;

  const customPageColor = page?.extra_fields?.header_background_color;

  const moduleBackgroundColor = {
    '--module-background-color': customPageColor || module.background_color,
    '--module-toplist-background-color': customPageColor || module.background_color,
  };

  const showModuleIntro = module.module_introduction && module.module_introduction !== '<p></p>';

  const noSuspense = [
    'cards_v2',
    'content',
    'top_list',
    'anchor',
    'info_grid',
    'information_grid',
    'operator_information_block',
  ];

  const homepageFirstModule = index === 0 && page.path === '/';

  return (
    ModuleComponent && (
      <div
        id={module.name === 'archive' && module.module_title ? null : anchorLabel}
        style={module.background_color && moduleBackgroundColor}
        data-color="#e7663f"
        className={`${styles[moduleClassName] !== undefined ? styles[moduleClassName] || '' : ''} ${
          module.name || ''
        } ${styles.module || ''} ${showModuleIntro ? '' : styles.noModuleIntro}  ${
          module?.style ? styles[module.style] || '' : ''
        }  ${module?.style ? styles[module.style] || '' : ''} module`}
      >
        {module.name !== 'cards_v2' && !disableModuleIntro && (
          <>
            <ModuleTitle
              module={module}
              isHomepageFirstModule={homepageFirstModule}
              anchorLabel={module.name === 'archive' && anchorLabel}
            />
            {showModuleIntro && (
              <Content
                isModuleIntroduction={module.module_introduction}
                module={{ value: module.module_introduction }}
                isHomepageFirstModule={homepageFirstModule}
                modulePosition={modulePosition}
              />
            )}
          </>
        )}

        {noSuspense.includes(module.name) ? (
          <ModuleComponent
            module={module}
            page={page}
            pageContext={pageContext}
            serverData={serverData}
            {...extraProps()}
            modulePosition={modulePosition}
          />
        ) : (
          <Suspense fallback={<div className={`${styles[module.name]}`} />}>
            <ModuleComponent
              module={module}
              page={page}
              pageContext={pageContext}
              serverData={serverData}
              {...extraProps()}
              modulePosition={modulePosition}
            />
          </Suspense>
        )}

        {admin && <AdminButton page={page} module={module} modulePosition={modulePosition - 1} />}
      </div>
    )
  );
};

Modules.propTypes = {
  serverData: PropTypes.shape({}),
  module: PropTypes.shape({
    anchor_label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    module_title_tag: PropTypes.string,
    module_title: PropTypes.string,
    link_label: PropTypes.string,
    link_value: PropTypes.string,
    style: PropTypes.string,
  }),
  page: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  pageContext: PropTypes.shape({}),
};

export default Modules;
